const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const WEEKDAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const SLOT_DURATION_OPTIONS = [10, 15, 20, 30, 45, 60];

export function parseDisplayTimeToMinutes(timeString) {
  if (!timeString) return null;

  const normalized = timeString.trim();
  const hasMeridiem = normalized.includes("AM") || normalized.includes("PM");
  const [rawHour, rawMinute = "0"] = normalized
    .replace("AM", "")
    .replace("PM", "")
    .trim()
    .split(":");

  let hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  if (hasMeridiem) {
    if (normalized.includes("PM") && hour !== 12) hour += 12;
    if (normalized.includes("AM") && hour === 12) hour = 0;
  }

  return hour * 60 + minute;
}

export function formatMinutesToDisplay(totalMinutes) {
  const hour24 = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const period = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
}

export function buildDefaultWeeklyAvailability() {
  return WEEKDAY_KEYS.reduce((acc, day) => {
    acc[day] = {
      enabled: !["saturday", "sunday"].includes(day),
      start_time: "08:00",
      end_time: "17:00",
      slot_duration_minutes: 30,
    };
    return acc;
  }, {});
}

export function inferWeeklyAvailabilityFromTimeSlots(timeSlots = []) {
  if (!timeSlots.length) {
    return buildDefaultWeeklyAvailability();
  }

  const totals = timeSlots
    .map(parseDisplayTimeToMinutes)
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!totals.length) {
    return buildDefaultWeeklyAvailability();
  }

  const uniqueTotals = [...new Set(totals)];
  const gaps = uniqueTotals
    .slice(1)
    .map((value, index) => value - uniqueTotals[index])
    .filter((gap) => gap > 0);

  const slotDuration = gaps.length ? Math.min(...gaps) : 30;
  const startTotal = uniqueTotals[0];
  const endTotal = Math.min(uniqueTotals[uniqueTotals.length - 1] + slotDuration, 24 * 60);

  return WEEKDAY_KEYS.reduce((acc, day) => {
    acc[day] = {
      enabled: !["saturday", "sunday"].includes(day),
      start_time: `${String(Math.floor(startTotal / 60)).padStart(2, "0")}:${String(startTotal % 60).padStart(2, "0")}`,
      end_time: `${String(Math.floor(endTotal / 60)).padStart(2, "0")}:${String(endTotal % 60).padStart(2, "0")}`,
      slot_duration_minutes: slotDuration,
    };
    return acc;
  }, {});
}

export function normalizeWeeklyAvailability(rawAvailability, fallbackTimeSlots = []) {
  const source = rawAvailability || inferWeeklyAvailabilityFromTimeSlots(fallbackTimeSlots);

  return WEEKDAY_KEYS.reduce((acc, day) => {
    const item = source?.[day] || {};
    acc[day] = {
      enabled: Boolean(item.enabled),
      start_time: item.start_time || "08:00",
      end_time: item.end_time || "17:00",
      slot_duration_minutes: Number(item.slot_duration_minutes || 30),
    };
    return acc;
  }, {});
}

export function generateSlotsFromSchedule(schedule) {
  if (!schedule?.enabled) return [];

  const [startHour, startMinute] = (schedule.start_time || "08:00").split(":").map(Number);
  const [endHour, endMinute] = (schedule.end_time || "17:00").split(":").map(Number);
  const slotDuration = Number(schedule.slot_duration_minutes || 30);

  if (
    [startHour, startMinute, endHour, endMinute, slotDuration].some((value) => Number.isNaN(value)) ||
    slotDuration <= 0
  ) {
    return [];
  }

  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;
  if (endTotal <= startTotal) return [];

  const slots = [];
  for (let current = startTotal; current + slotDuration <= endTotal; current += slotDuration) {
    slots.push(formatMinutesToDisplay(current));
  }

  return slots;
}

export function buildLegacyTimeSlotsFromWeeklyAvailability(weeklyAvailability) {
  const normalized = normalizeWeeklyAvailability(weeklyAvailability);
  const seen = new Set();
  const slots = [];

  WEEKDAY_KEYS.forEach((day) => {
    generateSlotsFromSchedule(normalized[day]).forEach((slot) => {
      if (!seen.has(slot)) {
        seen.add(slot);
        slots.push(slot);
      }
    });
  });

  return slots;
}

export function getSlotsForDate(settings, date) {
  if (!date) return [];
  if (!settings?.weekly_availability) return settings?.time_slots || [];

  const weeklyAvailability = normalizeWeeklyAvailability(
    settings?.weekly_availability,
    settings?.time_slots || []
  );
  const dayIndex = date.getDay();
  const weekdayKey = WEEKDAY_KEYS[(dayIndex + 6) % 7];
  return generateSlotsFromSchedule(weeklyAvailability[weekdayKey]);
}
